/**
 * visualTemplates.jsx
 * code_fx 스텝용 사전 정의 시각 템플릿 렌더러 모음.
 * AI는 visual_template 이름만 선택하고, 프론트엔드에서 렌더링 담당.
 */

import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// Weather Card Template
// 적합: API, HTTP 응답, 변수 조작 레슨
// 변수: temp, cond, hum
// ============================================
function WeatherCardTemplate({ values }) {
  const temp = parseInt(values.temp) || 0;
  const cond = String(values.cond || 'Sunny');
  const hum = parseInt(values.hum) || 0;
  const isHot = temp > 30;
  const isCold = temp < 0;

  const condIcon = {
    Sunny: 'fa-sun',
    Cloudy: 'fa-cloud',
    Snowy: 'fa-snowflake',
    Rainy: 'fa-cloud-rain',
    Windy: 'fa-wind',
  };
  const icon = condIcon[cond] || 'fa-sun';

  return (
    <div className="flex flex-col items-center justify-center h-full relative overflow-hidden transition-colors duration-700 rounded-xl"
      style={{
        background: isHot
          ? 'linear-gradient(135deg, #451a03, #7c2d12, #1e1b4b)'
          : isCold
            ? 'linear-gradient(135deg, #0f172a, #1e3a8a, #172554)'
            : 'linear-gradient(135deg, #0f172a, #1e293b)',
      }}
    >
      <div className="z-10 text-center space-y-4 p-8">
        <motion.div
          key={cond}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={isHot ? 'animate-pulse' : ''}
        >
          <i className={`fas ${icon} text-7xl ${isHot ? 'text-orange-400' : isCold ? 'text-cyan-300' : 'text-yellow-400'}`} />
        </motion.div>
        <motion.div
          key={temp}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-6xl font-bold text-white font-mono"
        >
          {temp}°C
        </motion.div>
        <div className="flex gap-4 justify-center text-sm font-mono bg-black/30 p-3 rounded-lg backdrop-blur-sm">
          <span className="text-blue-300">
            <i className="fas fa-droplet mr-1" />{hum}%
          </span>
          <span className="text-yellow-300">
            <i className={`fas ${icon} mr-1`} />{cond}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Style Preview Template
// 적합: HTML/CSS, 스타일링 레슨
// 변수: color, size, font, bg, text
// ============================================
function StylePreviewTemplate({ values }) {
  const color = values.color || '#22d3ee';
  const size = values.size || '16';
  const font = values.font || 'sans-serif';
  const bg = values.bg || '#1e293b';
  const text = values.text || 'Hello World!';
  const padding = values.padding || '';
  const radius = values.radius || '';
  const border = values.border || '';
  const width = values.width || '';
  const opacity = values.opacity || '';

  // 동적 스타일 구성 — 값이 있는 속성만 적용
  const dynamicStyle = {
    color,
    fontSize: `${size}px`,
    fontFamily: font,
    backgroundColor: bg,
  };
  dynamicStyle.padding = padding || '32px';
  if (radius) dynamicStyle.borderRadius = radius;
  if (border) dynamicStyle.border = border;

  // [Fix] width가 지정되면 min-width 제한 해제 (작게 줄어들 수 있도록)
  if (width) {
    dynamicStyle.width = width;
  } else {
    dynamicStyle.minWidth = '280px';
  }

  if (opacity) dynamicStyle.opacity = opacity;

  // 하단 라벨 — 실제 사용된 변수만 표시
  const labels = Object.entries(values)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => ({ key: k, display: `${k}: ${v}` }));

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 space-y-6">
      <div className="text-xs font-mono text-slate-500 uppercase tracking-wider">Style Preview</div>
      <motion.div
        key={JSON.stringify(values)}
        initial={{ scale: 0.95, opacity: 0.5 }}
        animate={{ scale: 1, opacity: 1 }}
        className="rounded-xl border border-slate-700 shadow-xl text-center"
        style={dynamicStyle}
      >
        {text}
      </motion.div>
      <div className="flex gap-3 flex-wrap justify-center text-xs font-mono">
        {labels.map(({ key, display }) => (
          <span key={key} className="bg-slate-800 px-2 py-1 rounded text-slate-400">{display}</span>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Profile Card Template
// 적합: 객체/딕셔너리, HR 데이터 레슨
// 변수: name, dept, role, age
// ============================================
function ProfileCardTemplate({ values }) {
  const name = values.name || '홍길동';
  const dept = values.dept || '개발팀';
  const role = values.role || '사원';
  const age = values.age || '28';

  return (
    <div className="flex items-center justify-center h-full p-8">
      <motion.div
        key={`${name}-${dept}-${role}`}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 shadow-xl w-72"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
            {name.charAt(0)}
          </div>
          <div>
            <div className="text-white font-bold text-lg">{name}</div>
            <div className="text-slate-400 text-sm">{role}</div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">부서</span>
            <span className="text-cyan-400 font-mono">{dept}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">직급</span>
            <span className="text-purple-400 font-mono">{role}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">나이</span>
            <span className="text-slate-300 font-mono">{age}세</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================
// Status Dashboard Template
// 적합: 서버 상태, 모니터링, 퍼센트 레슨
// 변수: status, value, label
// ============================================
function StatusDashboardTemplate({ values }) {
  const status = String(values.status || 'OK').toUpperCase();
  const value = parseInt(values.value) || 0;
  const label = values.label || 'System';

  const isOk = status === 'OK' || status === 'RUNNING' || status === '200';
  const isWarn = status === 'WARNING' || status === '404' || status === 'SLOW';

  return (
    <div className="flex items-center justify-center h-full p-8">
      <motion.div
        key={`${status}-${value}`}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 rounded-2xl p-8 border border-slate-700 shadow-xl w-80 space-y-6"
      >
        <div className="flex items-center justify-between">
          <span className="text-slate-400 text-sm font-mono uppercase">{label}</span>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${isOk ? 'bg-emerald-500/20 text-emerald-400' : isWarn ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
            {status}
          </span>
        </div>
        <div className="text-center">
          <div className="text-5xl font-bold font-mono text-white">{value}</div>
          <div className="text-slate-500 text-sm mt-1">Current Value</div>
        </div>
        <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
            transition={{ duration: 0.5 }}
            className={`h-full rounded-full ${isOk ? 'bg-emerald-500' : isWarn ? 'bg-yellow-500' : 'bg-red-500'}`}
          />
        </div>
      </motion.div>
    </div>
  );
}

// ============================================
// Console Output Template
// 적합: print(), f-string, 문자열 포매팅 레슨
// 변수: output (자유 텍스트)
// ============================================
function ConsoleOutputTemplate({ values }) {
  const output = values.output || '';

  return (
    <div className="flex flex-col h-full p-6">
      <div className="flex items-center gap-2 mb-3 text-slate-500 text-xs font-mono">
        <i className="fas fa-terminal" /> Console Output
      </div>
      <div className="flex-1 bg-black rounded-xl border border-slate-800 p-5 font-mono text-sm overflow-y-auto">
        <div className="text-slate-500 mb-2">{'>>>'}</div>
        <motion.div
          key={output}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-emerald-400 whitespace-pre-wrap break-words"
        >
          {output || <span className="text-slate-600 italic">출력 없음</span>}
        </motion.div>
      </div>
    </div>
  );
}

// ============================================
// Color Swatch Template
// 적합: RGB, hex 색상, CSS 색상 레슨
// 변수: r, g, b 또는 hex
// ============================================
function ColorSwatchTemplate({ values }) {
  let color;
  if (values.hex) {
    color = values.hex;
  } else {
    const r = parseInt(values.r) || 0;
    const g = parseInt(values.g) || 0;
    const b = parseInt(values.b) || 0;
    color = `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 space-y-6">
      <motion.div
        key={color}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-40 h-40 rounded-2xl border-4 border-slate-700 shadow-2xl"
        style={{ backgroundColor: color }}
      />
      <div className="text-center space-y-2">
        <div className="text-2xl font-mono font-bold text-white">{color}</div>
        {!values.hex && (
          <div className="flex gap-3 text-sm font-mono">
            <span className="text-red-400">R: {values.r || 0}</span>
            <span className="text-green-400">G: {values.g || 0}</span>
            <span className="text-blue-400">B: {values.b || 0}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// List Visualizer Template
// 적합: 리스트/배열 조작 시각화 (append, pop, sort 등)
// 변수: data (리스트/배열)
// ============================================
function ListVisualizerTemplate({ values }) {
  // 표준 키(data/items/list) 우선 → 못 찾으면 values 내 아무 배열이나 사용
  const rawData = values.data ?? values.items ?? values.list
    ?? Object.values(values).find(v => Array.isArray(v));
  const data = useMemo(() => {
    if (Array.isArray(rawData)) return rawData;
    if (typeof rawData === 'string' && rawData.trim().startsWith('[')) {
      try {
        const jsonStr = rawData.replace(/'/g, '"');
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed)) return parsed;
      } catch { /* fall through */ }
    }
    return [];
  }, [rawData]);

  return (
    <div className="flex flex-col h-full p-6 relative">
      <div className="flex items-center gap-2 mb-6 text-slate-500 text-xs font-mono uppercase tracking-wider">
        <i className="fas fa-list-ol" /> List Visualizer
      </div>

      <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto">
        <div className="flex flex-wrap gap-4 justify-center items-start content-start max-w-full p-4">
          <AnimatePresence mode="popLayout">
            {data.map((item, index) => (
              <motion.div
                key={`${index}-${item}`} // Composite key to force re-render on value change but keep some stability
                layout
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="flex flex-col items-center gap-2 group min-w-[64px]"
              >
                <div className="w-16 h-16 bg-slate-800 border-2 border-slate-600 rounded-xl flex items-center justify-center text-lg font-bold text-white shadow-lg group-hover:border-purple-500 group-hover:shadow-purple-500/20 transition-all overflow-hidden break-all p-1 text-center leading-tight">
                  {String(item)}
                </div>
                <div className="text-[10px] font-mono text-slate-500 bg-slate-900/50 px-2 rounded">
                  idx: {index}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {data.length === 0 && (
            <div className="text-slate-600 italic">비어있는 리스트입니다</div>
          )}
        </div>
      </div>

      <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-800 font-mono text-[10px] text-slate-400 break-all max-h-20 overflow-y-auto">
        Current List: {JSON.stringify(data)}
      </div>
    </div>
  );
}

// ============================================
// Flow Chart Template
// 적합: 조건문/반복문 실행 흐름 시각화
// 변수: _trace (실행된 노드 ID 리스트) 또는 current_node
// Config: nodes (id, label, x, y), edges (from, to, label)
// ============================================
function FlowChartTemplate({ values, config }) {
  const trace = Array.isArray(values._trace) ? values._trace : [];
  const currentNode = trace.length > 0 ? trace[trace.length - 1] : (values.current_node || null);

  const nodes = config?.nodes || [];
  const edges = config?.edges || [];

  return (
    <div className="relative w-full h-full bg-[#0f172a] overflow-hidden select-none">
      <div className="absolute top-4 left-4 text-xs font-mono text-slate-500 uppercase tracking-wider z-10 pointer-events-none">
        <i className="fas fa-project-diagram" /> Execution Flow
      </div>

      {/* SVG Container for Edges */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="20" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#475569" />
          </marker>
          <marker id="arrowhead-active" markerWidth="6" markerHeight="4" refX="20" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#a855f7" />
          </marker>
        </defs>
        {edges.map((edge, i) => {
          const fromNode = nodes.find(n => n.id === edge.from);
          const toNode = nodes.find(n => n.id === edge.to);
          if (!fromNode || !toNode) return null;

          // Check if this edge is active (if both nodes are in trace sequence)
          // Simplified: active if 'from' is current or in trace?
          // Actually flow visualization usually highlights the path.
          // Let's keep it simple: gray edges always.

          return (
            <g key={i}>
              <line
                x1={`${fromNode.x}%`} y1={`${fromNode.y}%`}
                x2={`${toNode.x}%`} y2={`${toNode.y}%`}
                stroke="#334155"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
              />
              {edge.label && (
                <text
                  x={(fromNode.x + toNode.x) / 2 + '%'}
                  y={(fromNode.y + toNode.y) / 2 + '%'}
                  dy="-5"
                  textAnchor="middle"
                  className="fill-slate-500 text-[10px] font-mono"
                >
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Nodes Layer */}
      {nodes.map(node => {
        const isActive = String(node.id) === String(currentNode);
        const isVisited = trace.includes(String(node.id));

        return (
          <motion.div
            key={node.id}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-lg border-2 text-xs font-bold shadow-lg transition-all duration-300 flex items-center justify-center text-center
              ${isActive
                ? 'bg-purple-600 border-purple-400 text-white scale-110 z-20 shadow-purple-500/50'
                : isVisited
                  ? 'bg-slate-700 border-slate-500 text-slate-200'
                  : 'bg-slate-800 border-slate-700 text-slate-500'
              }`}
            style={{ left: `${node.x}%`, top: `${node.y}%`, minWidth: '80px' }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: isActive ? 1.1 : 1 }}
          >
            {node.label}
          </motion.div>
        );
      })}
    </div>
  );
}

// ============================================
// Component Tree Template
// 적합: HTML/React 컴포넌트 구조 시각화
// 변수: code (전체 코드)
// ============================================
function ComponentTreeTemplate({ code }) {
  // Recursively build tree structure
  const buildTree = (el) => {
    if (el.nodeType !== 1) return null; // Element nodes only
    const children = Array.from(el.childNodes)
      .map(buildTree)
      .filter(Boolean);

    return {
      tag: el.tagName.toLowerCase(),
      id: el.id,
      classes: Array.from(el.classList),
      children
    };
  };

  const tree = useMemo(() => {
    try {
      if (!code) return null;
      // Extract body content if full HTML structure is provided, else use as fragment
      let content = code;

      // Simple heuristic to extract body content if <body> exists
      const bodyMatch = code.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch) {
        content = bodyMatch[1];
      }

      // Use DOMParser
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");
      const rootChildren = Array.from(doc.body.children).map(buildTree).filter(Boolean);
      return rootChildren;
    } catch (e) {
      return [];
    }
  }, [code]);

  const renderNode = (node, depth = 0) => (
    <motion.div
      key={`${node.tag}-${depth}-${Math.random()}`} // Note: keys might be unstable on re-parse
      initial={{ opacity: 0, x: -5 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: depth * 0.05 }}
      className="flex flex-col gap-1"
    >
      <div className="flex items-center gap-2 p-1.5 rounded bg-slate-800/50 border border-slate-700/50 hover:border-purple-500/50 hover:bg-slate-800 transition-colors self-start">
        <span className="text-purple-400 font-bold font-mono text-xs">&lt;{node.tag}&gt;</span>
        {node.id && <span className="text-yellow-400 text-[10px]">#{node.id}</span>}
        {node.classes.length > 0 && <span className="text-cyan-400 text-[10px]">.{node.classes.join('.')}</span>}
      </div>

      {node.children.length > 0 && (
        <div className="pl-4 border-l border-slate-800 ml-2 flex flex-col gap-1 mt-1">
          {node.children.map((child, i) => (
            <div key={i}>{renderNode(child, depth + 1)}</div>
          ))}
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="h-full p-6 overflow-auto custom-scrollbar">
      <div className="flex items-center gap-2 mb-4 text-slate-500 text-xs font-mono uppercase tracking-wider sticky top-0 bg-[#020617] py-2 z-10">
        <i className="fas fa-sitemap" /> Component Tree
      </div>
      <div className="flex flex-col gap-2 pb-10">
        {tree && tree.length > 0 ? (
          tree.map((node, i) => (
            <div key={i}>{renderNode(node)}</div>
          ))
        ) : (
          <div className="text-slate-600 italic text-sm text-center mt-10">
            구조를 분석할 수 없습니다.<br />HTML 태그를 입력해보세요.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Generic Key-Value Template (Fallback)
// 모든 추출 변수를 라벨+값으로 표시
// ============================================
function GenericKVTemplate({ values }) {
  const entries = Object.entries(values).filter(([, v]) => v !== undefined && v !== '');

  return (
    <div className="flex items-center justify-center h-full p-8">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-slate-900 rounded-2xl p-8 border border-slate-700 shadow-xl w-80 space-y-4"
      >
        <div className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-4">
          <i className="fas fa-code mr-2" />Extracted Values
        </div>
        {entries.length > 0 ? entries.map(([key, val]) => (
          <motion.div
            key={key}
            initial={{ x: -10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="flex justify-between items-center py-2 border-b border-slate-800 last:border-0"
          >
            <span className="text-slate-400 text-sm">{key}</span>
            <span className="text-cyan-400 font-mono font-bold">{String(val)}</span>
          </motion.div>
        )) : (
          <div className="text-slate-600 text-sm italic text-center py-4">값을 수정해보세요!</div>
        )}
      </motion.div>
    </div>
  );
}

// ============================================
// 변수명 정규화 (AI가 잘못된 이름을 쓸 경우 대비)
// ============================================
const TEMPLATE_VALID_NAMES = {
  weather_card: new Set(['temp', 'cond', 'hum']),
  style_preview: new Set(['color', 'size', 'font', 'bg', 'text', 'padding', 'radius', 'border', 'width', 'opacity']),
  profile_card: new Set(['name', 'dept', 'role', 'age']),
  status_dashboard: new Set(['status', 'value', 'label']),
  console_output: new Set(['output']),
  color_swatch: new Set(['r', 'g', 'b', 'hex']),
  list_visualizer: new Set(['data', 'items', 'list']),
  flow_chart: new Set(['_trace', 'current_node']),
  component_tree: new Set([]), // Uses 'code' prop, no extractions needed
};

const FUZZY_NAME_MAP = {
  temperature: 'temp', temp_c: 'temp', temp_f: 'temp',
  condition: 'cond', weather: 'cond', sky: 'cond',
  humidity: 'hum', humid: 'hum',
  font_size: 'size', fontsize: 'size', fontsize: 'size',
  font_family: 'font', fontfamily: 'font',
  background: 'bg', backgroundcolor: 'bg', background_color: 'bg', bgcolor: 'bg',
  border_radius: 'radius', borderradius: 'radius',
  border_color: 'border', bordercolor: 'border',
  department: 'dept', team: 'dept',
  position: 'role', job: 'role', job_title: 'role',
  state: 'status', code: 'status', status_code: 'status',
  count: 'value', score: 'value', percent: 'value', percentage: 'value',
  result: 'output', stdout: 'output', message: 'output', msg: 'output',
  red: 'r', green: 'g', blue: 'b',
  array: 'data', list: 'data', items: 'data', elements: 'data',
  trace: '_trace', node: 'current_node',
};

/**
 * AI가 생성한 변수명을 해당 template이 기대하는 변수명으로 정규화.
 * 매핑 실패한 변수는 그대로 전달 (generic_kv에서 처리).
 */
export function normalizeValues(templateName, values) {
  const validNames = TEMPLATE_VALID_NAMES[templateName];
  if (!validNames) return values; // generic_kv 등 제한 없는 템플릿

  const normalized = {};
  for (const [key, val] of Object.entries(values)) {
    if (validNames.has(key)) {
      normalized[key] = val;
    } else {
      const mapped = FUZZY_NAME_MAP[key] || FUZZY_NAME_MAP[key.toLowerCase()];
      if (mapped && validNames.has(mapped)) {
        normalized[mapped] = val;
      } else {
        normalized[key] = val; // 매핑 불가 시 그대로 전달
      }
    }
  }
  return normalized;
}

// ============================================
// Template Dispatcher
// ============================================
const TEMPLATE_MAP = {
  weather_card: WeatherCardTemplate,
  style_preview: StylePreviewTemplate,
  profile_card: ProfileCardTemplate,
  status_dashboard: StatusDashboardTemplate,
  console_output: ConsoleOutputTemplate,
  color_swatch: ColorSwatchTemplate,
  generic_kv: GenericKVTemplate,
  list_visualizer: ListVisualizerTemplate,
  flow_chart: FlowChartTemplate,
  component_tree: ComponentTreeTemplate,
};

export function getVisualTemplate(name) {
  return TEMPLATE_MAP[name] || TEMPLATE_MAP.generic_kv;
}
